// useCotizacionFormXL - Sprint 44 Fase 3 Vista 4.
// Hook del formulario de cotizacion XL. Reusa cotizacionesService legacy.
// Funcionalidad: load por :id o blank, save (POST/PUT), cambiar estado,
// convertir a venta. msg auto-dismiss 4s. Backend intacto.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as svc from '../../cotizaciones/services/cotizacionesService';

const MSG_AUTO_DISMISS_MS = 4000;

const EMPTY = {
  id: null,
  numero: '',
  cliente_id: null,
  fecha: '',
  moneda: 'COP',
  validez_dias: 15,
  observaciones: '',
  estado: 'borrador',
  total: 0,
  subtotal: 0,
  iva_total: 0,
  precio_incluye_iva: false,
  venta_id: null,
  created_at: null,
  updated_at: null,
  usuario_creacion: null,
  cliente: null,
};

export default function useCotizacionFormXL() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'nueva';

  const [form, setForm]     = useState(EMPTY);
  const [tab, setTab]       = useState('datos');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [acting, setActing] = useState(false);
  const [msg, setMsg]       = useState(null);

  const msgTimer = useRef(null);
  const pushMsg = useCallback((kind, text) => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
    setMsg({ kind, text });
    msgTimer.current = setTimeout(() => setMsg(null), MSG_AUTO_DISMISS_MS);
  }, []);
  useEffect(() => () => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (isNew) {
        try {
          const numero = await svc.nextNumero();
          if (alive) setForm({
            ...EMPTY, numero,
            fecha: new Date().toISOString().slice(0, 10),
          });
        } catch (_) {
          if (alive) setForm({
            ...EMPTY,
            fecha: new Date().toISOString().slice(0, 10),
          });
        }
        if (alive) setLoaded(true);
        return;
      }
      try {
        const d = await svc.getCotizacion(id);
        if (alive) setForm({ ...EMPTY, ...(d || {}) });
      } catch (e) {
        if (alive) pushMsg('err', 'No se pudo cargar la cotizacion');
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [id, isNew, pushMsg]);

  const set = useCallback((k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
  }, []);

  const buildPayload = useCallback(() => {
    const p = { ...form };
    if (p.validez_dias === '' || p.validez_dias == null) p.validez_dias = null;
    else p.validez_dias = Number(p.validez_dias);
    if (!p.fecha) p.fecha = null;
    delete p.cliente;
    return p;
  }, [form]);

  const onSave = useCallback(async () => {
    if (!form.cliente_id) {
      pushMsg('err', 'Selecciona un cliente');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isNew) {
        const created = await svc.createCotizacion(payload);
        pushMsg('ok', 'Cotizacion creada');
        if (created && created.id) {
          navigate('/cotizaciones/' + created.id, { replace: true });
        }
      } else {
        const updated = await svc.updateCotizacion(id, payload);
        if (updated) setForm((f) => ({ ...f, ...updated }));
        pushMsg('ok', 'Cotizacion actualizada');
      }
    } catch (e) {
      pushMsg('err', (e && e.message) || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }, [isNew, id, form.cliente_id, buildPayload, navigate, pushMsg]);

  const onCambiarEstado = useCallback(async (nuevo) => {
    if (isNew) return;
    setActing(true);
    try {
      const r = await svc.cambiarEstado(id, nuevo);
      if (r) setForm((f) => ({ ...f, ...r }));
      pushMsg('ok', 'Estado: ' + nuevo);
    } catch (e) {
      pushMsg('err', (e && e.message) || 'Error al cambiar estado');
    } finally {
      setActing(false);
    }
  }, [isNew, id, pushMsg]);

  const onConvertir = useCallback(async () => {
    if (isNew) return;
    if (!window.confirm('Convertir esta cotizacion en una venta (borrador)?')) return;
    setConverting(true);
    try {
      const r = await svc.convertirAVenta(id);
      const vid = r && r.venta_id;
      pushMsg('ok', 'Venta creada');
      if (vid) navigate('/ventas/' + vid);
    } catch (e) {
      pushMsg('err', (e && e.message) || 'Error al convertir');
    } finally {
      setConverting(false);
    }
  }, [isNew, id, navigate, pushMsg]);

  const onRefresh = useCallback(async () => {
    if (isNew) return;
    try {
      const d = await svc.getCotizacion(id);
      if (d) setForm((f) => ({ ...f, ...d }));
    } catch (_) { /* no bloquea */ }
  }, [isNew, id]);

  return {
    id, isNew, navigate,
    form, set, tab, setTab,
    loaded, saving, converting, acting, msg,
    onSave, onCambiarEstado, onConvertir, onRefresh,
    clearMsg: () => setMsg(null),
  };
}
